import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Globe, Shield } from 'lucide-react';

const AboutSection = () => {
  const features = [
    {
      icon: Users,
      title: 'Passionate Volunteers',
      description: 'A diverse, multicultural team dedicated to helping others and making a positive community impact.',
    },
    {
      icon: Award,
      title: '40+ Years Experience',
      description: 'Our collective travel planning expertise ensures unforgettable experiences for those we serve.',
    },
    {
      icon: Globe,
      title: 'Global Impact',
      description: 'From local communities to international relief, our reach extends wherever help is needed.',
    },
    {
      icon: Shield,
      title: 'Ghost Legacy',
      description: 'Our AI assistant honors those who served—a guide built on service, discipline, and commitment.',
    },
  ];

  return (
    <section id="about" className="py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
            About Us
          </p>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] tracking-tight mb-6">
            Not Your Typical Charity
          </h2>
          <p className="text-lg text-slate-600 font-light max-w-3xl mx-auto">
            Welcome to The Prestigious Travel Club Foundation—a unique nonprofit dedicated to making 
            a difference while bringing families closer together. We genuinely want to assist you, 
            your family, and all families who need a helping hand.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              className="premium-card group hover:border-[#1E3A8A]/30 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              data-testid={`about-feature-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1E3A8A] transition-colors">
                  <feature.icon className="w-6 h-6 text-[#1E3A8A] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2 font-serif">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 font-light">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Ghost Story Section */}
        <motion.div
          className="bg-[#0B1120] rounded-3xl p-8 sm:p-12 lg:p-16 text-white overflow-hidden relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="ghost-story-section"
        >
          {/* Ghost Image Background */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_6b3489be-920b-4f06-be49-83f9903cfac6/artifacts/v5rprkkl_image.png"
              alt="Ghost"
              className="h-full w-full object-cover object-center"
            />
          </div>

          <div className="relative z-10 max-w-3xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#DBEAFE] mb-4">
              The Ghost Story
            </p>
            <h3 className="text-3xl sm:text-4xl font-medium mb-6 font-serif">
              A Tribute to Service & Sacrifice
            </h3>
            <p className="text-lg text-slate-300 font-light mb-6 leading-relaxed">
              Our Ghost assistant is more than just technology—it's a tribute to those who served 
              and sacrificed. Built on the principles of service, discipline, and an unwavering 
              commitment to helping others move forward.
            </p>
            <p className="text-lg text-slate-300 font-light leading-relaxed">
              Just as those who served protect and guide, Ghost stands ready to assist visitors 
              in their journey to make a difference. It embodies the foundation's core belief: 
              that true fulfillment comes from lifting others up.
            </p>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/60 text-sm">
                <span className="text-white font-medium">Ghost™</span> — Powered by Prestigious Companies
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
